import { BaseModule } from "../base.module";

export class ProgramModule extends BaseModule {
  initialize() {
    const containers = document.querySelectorAll(".program-container");

    containers.forEach(container => new ProgramItem(container));
  }
}

class ProgramItem {
  constructor(container) {
    this.container = container;
    this.currentOpenSession = null;
    this.tableContainer = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.navigation = null;

    this.initialize();
  }

  initialize() {
    this.bindElements();
    this.bindEvents();
    this.initializeAccessibility();
    
    this.updateTableLayout();
    this.checkScrollability();
  }

  bindElements() {
    this.tableContainer = this.container.querySelector('.program-table-container');
    this.navigation = this.container.querySelector('.program-navigation');
    this.prevBtn = this.container.querySelector('.nav-arrow.prev');
    this.nextBtn = this.container.querySelector('.nav-arrow.next');
  }

  initializeAccessibility() {
    // Initialize all sessions as collapsed (not focusable)
    const sessionItems = this.container.querySelectorAll('.session-item');
    sessionItems.forEach(sessionItem => {
      const isExpanded = sessionItem.classList.contains('expanded');
      if (!isExpanded) {
        this.collapseSession(sessionItem);
      }
    });
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollTable(-1));
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollTable(1));
    }

    this.container.addEventListener("click", (e) => {
      // Don't toggle if clicking on a link
      if (e.target.closest('a')) {
        return;
      }

      const sessionItem = e.target.closest(".session-item");
      if (sessionItem) {
        const sessionId = sessionItem.dataset.session;
        this.toggleSessionExpansion(sessionId, sessionItem);
        return;
      }
    });

    this.container.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        const sessionItem = e.target.closest(".session-item");
        if (sessionItem && e.target === sessionItem) {
          e.preventDefault();
          const sessionId = sessionItem.dataset.session;
          this.toggleSessionExpansion(sessionId, sessionItem);
          return;
        }
      }
    });

    if (this.tableContainer) {
      this.tableContainer.addEventListener('scroll', () => {
        this.updateNavigationButtons();
      });
    }

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateTableLayout();
        this.checkScrollability();
      }, 10);
    });

  }

  toggleSessionExpansion(sessionId, clickedElement) {
    const isExpanded = clickedElement.classList.contains("expanded");

    if (isExpanded) {
      this.collapseSession(clickedElement);
    } else {
      this.expandSession(clickedElement);
    }
  }

  getBaseSessionId(sessionId) {
    return sessionId.includes('_rec_') ? sessionId.split('_rec_')[0] : sessionId;
  }

  collapseAllSessionInstances(baseSessionId) {
    const allSessionElements = this.container.querySelectorAll('.session-item');
    allSessionElements.forEach(element => {
      const elementSessionId = element.dataset.session;
      const elementBaseId = this.getBaseSessionId(elementSessionId);
      if (elementBaseId === baseSessionId) {
        element.classList.remove("expanded");
      }
    });
  }

  expandSession(sessionElement) {
    sessionElement.classList.add("expanded");
    sessionElement.setAttribute("aria-expanded", "true");
    
    // Make expanded content focusable
    const expandedContent = sessionElement.querySelector('.session-expanded-content');
    if (expandedContent) {
      expandedContent.removeAttribute('inert');
      expandedContent.setAttribute('aria-hidden', 'false');
      
      // Make links within expanded content focusable
      const links = expandedContent.querySelectorAll('a');
      links.forEach(link => {
        link.removeAttribute('tabindex');
      });
    }
  }

  collapseSession(sessionElement) {
    sessionElement.classList.remove("expanded");
    sessionElement.setAttribute("aria-expanded", "false");
    
    // Make expanded content not focusable
    const expandedContent = sessionElement.querySelector('.session-expanded-content');
    if (expandedContent) {
      expandedContent.setAttribute('inert', '');
      expandedContent.setAttribute('aria-hidden', 'true');
      
      // Make links within expanded content not focusable
      const links = expandedContent.querySelectorAll('a');
      links.forEach(link => {
        link.setAttribute('tabindex', '-1');
      });
    }
  }

  expandAllSessions(sessionId) {
    const sessionElements = this.container.querySelectorAll(
      `[data-session="${sessionId}"].session-item`
    );

    sessionElements.forEach(element => {
      element.classList.add("expanded");
    });

    this.currentOpenSession = sessionId;
  }

  collapseAllSessions(sessionId) {
    const sessionElements = this.container.querySelectorAll(
      `[data-session="${sessionId}"].session-item`
    );

    sessionElements.forEach(element => {
      element.classList.remove("expanded");
    });

    if (this.currentOpenSession === sessionId) {
      this.currentOpenSession = null;
    }
  }

  scrollTable(direction) {
    if (!this.tableContainer) return;

    const table = this.tableContainer.querySelector('.program-table');
    if (!table) return;

    const allColumns = table.querySelectorAll('th.date-column');
    if (allColumns.length === 0) return;

    const containerWidth = this.tableContainer.clientWidth;
    const totalColumns = allColumns.length;
    const maxVisibleColumns = this.getVisibleColumns();
    
    let columnWidth;
    if (totalColumns <= maxVisibleColumns) {
        columnWidth = containerWidth / totalColumns;
    } else {
      columnWidth = containerWidth / maxVisibleColumns;
    }

    const currentScroll = this.tableContainer.scrollLeft;
    
    const currentColumn = Math.round(currentScroll / columnWidth);
    
    const targetColumn = direction === 1 ? currentColumn + 1 : currentColumn - 1;
    
    const targetScroll = targetColumn * columnWidth;

    this.tableContainer.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }

  checkScrollability() {
    if (!this.tableContainer || !this.navigation) return;

    const table = this.tableContainer.querySelector('.program-table');
    if (!table) return;
    
    const allColumns = table.querySelectorAll('th.date-column');
    const totalColumns = allColumns.length;
    
    if (totalColumns <= 1) {
      this.navigation.style.display = 'none';
      return;
    }
    
    const maxVisibleColumns = this.getVisibleColumns();
    const columnWidth = Math.floor(this.tableContainer.clientWidth / maxVisibleColumns);
    const tableMinWidth = totalColumns * columnWidth;
    const containerWidth = this.tableContainer.clientWidth;
    
    table.style.minWidth = `${tableMinWidth}px`;
    
    const shouldShowNavigation = totalColumns > maxVisibleColumns;
    
    setTimeout(() => {
      const scrollWidth = this.tableContainer.scrollWidth;
      const clientWidth = this.tableContainer.clientWidth;
      const hasOverflow = scrollWidth > clientWidth + 1;
      
      if (shouldShowNavigation && hasOverflow) {
        this.navigation.style.display = 'flex';
        this.updateNavigationButtons();
      } else {
        this.navigation.style.display = 'none';
        this.tableContainer.scrollLeft = 0;
      }
    }, 10);
  }

  updateNavigationButtons() {
    if (!this.tableContainer || !this.prevBtn || !this.nextBtn) return;

    const scrollLeft = this.tableContainer.scrollLeft;
    const scrollWidth = this.tableContainer.scrollWidth;
    const clientWidth = this.tableContainer.clientWidth;
    
    const tolerance = 1;
    const isAtStart = scrollLeft <= tolerance;
    const isAtEnd = scrollLeft >= (scrollWidth - clientWidth - tolerance);

    this.prevBtn.disabled = isAtStart;
    this.nextBtn.disabled = isAtEnd;
  }

  getVisibleColumns() {
    const screenWidth = window.innerWidth;
    
    if (screenWidth >= 1024) {
      return 5;
    } else if (screenWidth >= 768) {
      return 3;
    } else {
      return 1;
    }
  }

  updateTableLayout() {
    if (!this.tableContainer) return;
    
    const table = this.tableContainer.querySelector('.program-table');
    if (!table) return;
    
    const containerWidth = this.tableContainer.clientWidth;
    const allColumns = table.querySelectorAll('th.date-column');
    const totalColumns = allColumns.length;
    const maxVisibleColumns = this.getVisibleColumns();
    
    if (totalColumns <= maxVisibleColumns) {
      table.style.width = '100%';
      table.style.tableLayout = 'fixed';
      
      const columnPercentage = (100 / totalColumns).toFixed(4);
      allColumns.forEach(col => {
        col.style.width = `${columnPercentage}%`;
      });
      
      const allCells = table.querySelectorAll('tbody td');
      allCells.forEach(cell => {
        cell.style.width = `${columnPercentage}%`;
      });
    } else {
      const columnWidth = containerWidth / maxVisibleColumns;
      const tableWidth = columnWidth * totalColumns;
      
      table.style.width = `${tableWidth}px`;
      table.style.tableLayout = 'fixed';
      
      allColumns.forEach(col => {
        col.style.width = `${columnWidth}px`;
      });
      
      const allCells = table.querySelectorAll('tbody td');
      allCells.forEach(cell => {
        cell.style.width = `${columnWidth}px`;
      });
    }
  }

  destroy() {
    if (this.currentOpenSession) {
      this.collapseAllSessions(this.currentOpenSession);
    }
  }
}